import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  Collapse,
  IconButton,
  Divider,
  CircularProgress,
  Switch,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import FilterListIcon from '@mui/icons-material/FilterList'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { JURISDICTION_TREE } from '../../../constants/legalDocumentTypes'
import { fetchJurisdictionTree } from '../../../redux/actions/userAction'
import { getDocTypeColor } from '../../../utils/utils'
import {
  setSearchFilters,
  clearSearchFilters,
  setDateFilter,
  clearDateFilter,
  clearDateSort,
  paginateSearch,
  filteredSearchCitations,
  setFilterPanelOpen,
  setIsAbrogeFilter,
} from '../../../redux/actions/searchAction'
import ReactGA from 'react-ga4'
import { tracker } from '../../../analytics'
import { useSearchTokens } from '../searchDesignTokens'

const SIDEBAR_WIDTH = 280

// ─── Count badge ─────────────────────────────────────────────────────────────

// Adapt the backend jurisdiction tree (GET jurisdiction-tree, cached in
// state.user.jurisdictionTrees) to the sidebar's shape. Facet values are the
// English jurisdiction names, so match on name.en. Falls back to the static
// tree when the backend tree isn't loaded yet.
const toSidebarTree = (treeData) => {
  if (!treeData?.has_sub_jurisdictions || !Array.isArray(treeData.tree)) return JURISDICTION_TREE
  const enName = (n) => (n && typeof n === 'object' ? n.en || Object.values(n)[0] : n)
  const labels = { federal: 'Federal', emirate: 'Emirates', free_zone: 'Free Zones' }
  return treeData.tree.map((group) => {
    if (group.level === 'free_zone') {
      return {
        level: group.level,
        label: labels.free_zone,
        children: group.children.map((pg) => ({
          parent: enName(pg.parent_name),
          zones: pg.zones.map((z) => ({ name: enName(z.name) })),
        })),
      }
    }
    return {
      level: group.level,
      label: labels[group.level] || group.level,
      children: group.children.map((c) => ({ name: enName(c.name) })),
    }
  })
}

const CountBadge = ({ count, dk }) => (
  <Typography
    component="span"
    sx={{
      fontSize: '0.68rem',
      fontWeight: 600,
      color: dk.text3,
      backgroundColor: dk.chipBg,
      borderRadius: '10px',
      px: 0.8,
      py: '1px',
      ml: 'auto',
      minWidth: 20,
      textAlign: 'center',
      lineHeight: 1.6,
    }}
  >
    {count}
  </Typography>
)

// ─── Filter item row (checkbox + label + count) ──────────────────────────────

const FilterItem = ({ label, count, checked, onChange, color, dk, indent = 0 }) => (
  <Box
    onClick={onChange}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.4,
      px: 1,
      pl: 1 + indent * 2,
      cursor: 'pointer',
      borderRadius: '6px',
      transition: 'background-color 0.15s',
      '&:hover': {
        backgroundColor: dk.hover,
      },
    }}
  >
    <Checkbox
      checked={checked}
      size="small"
      sx={{
        p: 0.3,
        color: color || dk.disabled,
        '&.Mui-checked': {
          color: color || dk.gold,
        },
        '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
      }}
    />
    {color && (
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
    )}
    <Typography
      sx={{
        fontSize: '0.78rem',
        fontWeight: checked ? 600 : 400,
        color: checked ? dk.text1 : dk.text2,
        flexGrow: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    {count > 0 && <CountBadge count={count} dk={dk} />}
  </Box>
)

// ─── Accordion section header ────────────────────────────────────────────────

const SectionHeader = ({ icon, title, expanded, onToggle, activeCount, dk }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 1,
      px: 1,
      cursor: 'pointer',
      borderRadius: '8px',
      userSelect: 'none',
      transition: 'background-color 0.15s',
      '&:hover': {
        backgroundColor: dk.hover,
      },
    }}
  >
    {React.cloneElement(icon, {
      sx: {
        fontSize: '1rem',
        color: activeCount > 0 ? dk.gold : dk.text2,
      },
    })}
    <Typography
      sx={{
        fontSize: '0.76rem',
        fontWeight: 700,
        color: dk.text1,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        flexGrow: 1,
      }}
    >
      {title}
    </Typography>
    {activeCount > 0 && (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: dk.gold,
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.62rem',
          fontWeight: 700,
        }}
      >
        {activeCount}
      </Box>
    )}
    {expanded ? (
      <ExpandMoreIcon sx={{ fontSize: '1rem', color: dk.disabled }} />
    ) : (
      <ChevronRightIcon sx={{ fontSize: '1rem', color: dk.disabled }} />
    )}
  </Box>
)

// ─── Main component ──────────────────────────────────────────────────────────

const SearchFilterSidebar = ({ mode }) => {
  const dispatch = useDispatch()
  const dk = useSearchTokens()
  const { t } = useTranslation()

  const countryPref = useSelector((state) => state.user?.countryPreference)
  const aeTreeData = useSelector((state) => state.user?.jurisdictionTrees?.AE)
  const jurisdictionTree = React.useMemo(() => toSidebarTree(aeTreeData), [aeTreeData])
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA')
  const hasAE = Array.isArray(countryPref) ? countryPref.includes('AE') : countryPref === 'AE'
  useEffect(() => {
    if (hasAE && !aeTreeData) dispatch(fetchJurisdictionTree('AE'))
  }, [hasAE, aeTreeData, dispatch])
  const searchFacets = useSelector((state) => state.search.searchFacets || {})
  const searchFilters = useSelector((state) => state.search.searchFilters || {})
  const currentSearchId = useSelector((state) => state.search.currentSearchId)
  const isFilterPanelOpen = useSelector((state) => state.search.isFilterPanelOpen)
  const isLoadingSearchCitations = useSelector((state) => state.search.isLoadingSearchCitations)
  const isAbrogeFilter = useSelector((state) => state.search.isAbrogeFilter)
  const searchQuery = useSelector((state) => state.search.searchQuery)
  const dateFilter = useSelector((state) => state.search.dateFilter)

  const scrollResultsToTop = () => {
    const resultsContainer = document.querySelector('[data-results-scroll]')
    if (resultsContainer) {
      resultsContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleIsAbrogeChange = (event) => {
    const checked = event.target.checked
    dispatch(setIsAbrogeFilter(checked))
    ReactGA.event({
      category: 'Filter',
      action: 'User toggled is_active filter',
      label: checked ? 'Enabled' : 'Disabled'
    })
    tracker.event({
      category: 'Filter',
      action: 'User toggled is_active filter',
      label: checked ? 'Enabled' : 'Disabled'
    })
    if (searchQuery) {
      dispatch(clearSearchFilters())
      dispatch(filteredSearchCitations(searchQuery, mode, checked ? { is_active: true } : null))
    }
    scrollResultsToTop()
  }

  const handleDateChange = (field, value) => {
    const current = dateFilter || {}
    const newStart = field === 'start' ? value : (current.startDate ? dayjs(current.startDate) : null)
    const newEnd = field === 'end' ? value : (current.endDate ? dayjs(current.endDate) : null)

    if (!newStart && !newEnd) {
      dispatch(clearDateFilter())
      if (currentSearchId) {
        const filtersToSend = Object.keys(searchFilters).length > 0 ? searchFilters : null
        dispatch(paginateSearch(currentSearchId, filtersToSend, 1))
      }
      return
    }

    const filterData = {
      startDate: newStart ? newStart.toDate().toISOString() : null,
      endDate: newEnd ? newEnd.toDate().toISOString() : null,
    }
    dispatch(setDateFilter(filterData))

    if (currentSearchId) {
      const allFilters = {
        ...searchFilters,
        ...(filterData.startDate ? { date_range_start: filterData.startDate } : {}),
        ...(filterData.endDate ? { date_range_end: filterData.endDate } : {}),
      }
      dispatch(paginateSearch(currentSearchId, allFilters, 1))
    }
    scrollResultsToTop()
  }

  const handleClearDateFilter = () => {
    dispatch(clearDateFilter())
    dispatch(clearDateSort())
    if (currentSearchId) {
      const filtersToSend = Object.keys(searchFilters).length > 0 ? searchFilters : null
      dispatch(paginateSearch(currentSearchId, filtersToSend, 1))
    }
    scrollResultsToTop()
  }

  const dateActiveCount = (dateFilter?.startDate || dateFilter?.endDate) ? 1 : 0

  const [stableFacets, setStableFacets] = useState(searchFacets)
  useEffect(() => {
    const hasData =
      searchFacets.doc_types?.length > 0 ||
      searchFacets.jurisdiction_names?.length > 0 ||
      searchFacets.legal_domains?.length > 0 ||
      searchFacets.case_type_categories?.length > 0 ||
      searchFacets.source_platforms?.length > 0
    if (hasData) {
      setStableFacets(searchFacets)
    }
  }, [searchFacets])

  const [expandedSections, setExpandedSections] = useState({
    dateRange: false,
    docTypes: true,
    jurisdiction: true,
    caseTypeCategories: true,
    legalDomains: true,
    sourcePlatforms: true,
  })
  const [expandedJurisdictionGroups, setExpandedJurisdictionGroups] = useState({})

  const buildCountMap = useCallback((facetArray) => {
    const map = {}
    if (!facetArray) return map
    facetArray.forEach(({ value, count }) => {
      map[value] = count
    })
    return map
  }, [])

  const docTypeCounts = buildCountMap(stableFacets.doc_types)
  const jurisdictionNameCounts = buildCountMap(stableFacets.jurisdiction_names)
  const legalDomainCounts = buildCountMap(stableFacets.legal_domains)

  // Jurisdictions not covered by the UAE jurisdiction tree (e.g. "Kingdom of Morocco")
  const treeNames = React.useMemo(() => {
    const s = new Set()
    jurisdictionTree.forEach((group) => {
      if (group.level === 'free_zone') {
        group.children.forEach((pg) => pg.zones.forEach((z) => s.add(z.name)))
      } else {
        group.children.forEach((child) => s.add(child.name))
      }
    })
    return s
  }, [jurisdictionTree])

  const flatJurisdictions = (stableFacets.jurisdiction_names || []).filter(
    ({ value }) => !treeNames.has(value)
  )

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const toggleJurisdictionGroup = (key) => {
    setExpandedJurisdictionGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleFilterToggle = useCallback(
    (filterKey, value) => {
      const currentValues = searchFilters[filterKey] || []
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]

      const newFilters = { ...searchFilters }
      if (newValues.length > 0) {
        newFilters[filterKey] = newValues
      } else {
        delete newFilters[filterKey]
      }

      dispatch(setSearchFilters(newFilters))

      if (currentSearchId) {
        const filtersToSend = Object.keys(newFilters).length > 0 ? newFilters : null
        dispatch(paginateSearch(currentSearchId, filtersToSend, 1))
      }
      scrollResultsToTop()
    },
    [searchFilters, currentSearchId, dispatch]
  )

  const isChecked = (filterKey, value) => {
    return (searchFilters[filterKey] || []).includes(value)
  }

  const getActiveCount = (filterKey) => {
    return (searchFilters[filterKey] || []).length
  }

  // ─── Collapsed state (thin strip with toggle) ──────────────────────────

  if (!isFilterPanelOpen) {
    return (
      <Box
        sx={{
          width: 40,
          minHeight: '100%',
          borderRight: `1px solid ${dk.border}`,
          backgroundColor: dk.surface,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => dispatch(setFilterPanelOpen(true))}
          sx={{
            color: dk.text2,
            '&:hover': { backgroundColor: dk.goldSub },
          }}
        >
          <FilterListIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Box>
    )
  }

  const hasDocTypes = stableFacets.doc_types?.length > 0
  const hasJurisdiction = stableFacets.jurisdiction_names?.length > 0 && !(countryPreference === 'MA' && mode === 'case-law')
  const hasCaseTypeCategories = stableFacets.case_type_categories?.length > 0
  const hasLegalDomains = stableFacets.legal_domains?.length > 0
  // Backend only returns the source_platforms facet for regulation searches,
  // so we trust its presence rather than re-checking the (potentially-stale) UI mode.
  const hasSourcePlatforms = stableFacets.source_platforms?.length > 0
  const hasAnyFacets = hasDocTypes || hasJurisdiction || hasCaseTypeCategories || hasLegalDomains || hasSourcePlatforms

  return (
    <Box
      data-tour="search-filter-sidebar"
      sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        minHeight: '100%',
        borderRight: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1.2,
          borderBottom: `1px solid ${dk.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <FilterListIcon
            sx={{ fontSize: '1rem', color: dk.text2 }}
          />
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              color: dk.text1,
            }}
          >
            {t('filters')}
          </Typography>
          {isLoadingSearchCitations && (
            <CircularProgress size={10} thickness={5} sx={{ color: dk.gold, ml: 0.5 }} />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => dispatch(setFilterPanelOpen(false))}
          sx={{
            color: dk.disabled,
            p: 0.3,
            '&:hover': { backgroundColor: dk.goldSub },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>

      {/* Scrollable filter content */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 0.5,
          py: 1,
          opacity: isLoadingSearchCitations ? 0.5 : 1,
          transition: 'opacity 0.2s',
          pointerEvents: isLoadingSearchCitations ? 'none' : 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: dk.scrollThumb,
            borderRadius: 2,
          },
        }}
      >
        {/* ── In force toggle ──────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.6,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.76rem',
              fontWeight: 700,
              color: dk.text1,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {t('en_vigueur')}
          </Typography>
          <Switch
            checked={isAbrogeFilter}
            onChange={handleIsAbrogeChange}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: dk.gold,
                '& + .MuiSwitch-track': { backgroundColor: dk.gold },
              },
              '& .MuiSwitch-track': { backgroundColor: dk.border },
            }}
          />
        </Box>
        <Divider sx={{ mx: 1, mb: 0.5, borderColor: dk.border }} />

        {/* ── Date Range ──────────────────────────────────────────── */}
        <SectionHeader
          icon={<CalendarTodayIcon />}
          title={t('date_filter')}
          expanded={expandedSections.dateRange}
          onToggle={() => toggleSection('dateRange')}
          activeCount={dateActiveCount}
          dk={dk}
        />
        <Collapse in={expandedSections.dateRange}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Box sx={{ px: 1, pb: 0.8, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {[
                { label: t('start_date'), field: 'start', value: dateFilter?.startDate, maxDate: dateFilter?.endDate, minDate: undefined },
                { label: t('end_date'), field: 'end', value: dateFilter?.endDate, maxDate: undefined, minDate: dateFilter?.startDate },
              ].map(({ label, field, value, maxDate, minDate }) => (
                <DatePicker
                  key={field}
                  label={label}
                  value={value ? dayjs(value) : null}
                  onChange={(val) => handleDateChange(field, val)}
                  maxDate={maxDate ? dayjs(maxDate) : undefined}
                  minDate={minDate ? dayjs(minDate) : undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          height: 32,
                          borderRadius: '6px',
                          backgroundColor: 'transparent',
                          color: dk.text1,
                          fontSize: '0.72rem',
                          '& fieldset': { borderColor: dk.border },
                          '&:hover fieldset': { borderColor: dk.borderHover },
                          '&.Mui-focused fieldset': { borderColor: dk.gold, borderWidth: 1 },
                        },
                        '& .MuiOutlinedInput-input': {
                          py: '5px',
                          px: 1,
                          fontSize: '0.72rem',
                        },
                        '& .MuiInputLabel-root': {
                          color: dk.text3,
                          fontSize: '0.72rem',
                          transform: 'translate(10px, 7px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(12px, -7px) scale(0.85)',
                          },
                          '&.Mui-focused': { color: dk.gold },
                        },
                        '& .MuiIconButton-root': {
                          color: dk.text3,
                          p: 0.3,
                          '& .MuiSvgIcon-root': { fontSize: '0.9rem' },
                        },
                      },
                    },
                  }}
                />
              ))}
              {dateActiveCount > 0 && (
                <Typography
                  onClick={handleClearDateFilter}
                  sx={{
                    fontSize: '0.68rem',
                    color: dk.text3,
                    cursor: 'pointer',
                    px: 0.5,
                    '&:hover': { color: dk.gold },
                  }}
                >
                  {t('clear')}
                </Typography>
              )}
            </Box>
          </LocalizationProvider>
        </Collapse>
        <Divider sx={{ mx: 1, mb: 0.5, borderColor: dk.border }} />

        {!hasAnyFacets && (
          <Typography
            variant="body2"
            sx={{ color: dk.disabled, textAlign: 'center', mt: 4, px: 2 }}
          >
            {t('search_to_see_filters')}
          </Typography>
        )}

        {/* ── Source Type ─────────────────────────────────────────────── */}
        {hasDocTypes && (
          <Box data-tour="search-filter-doc-types">
            <SectionHeader
              icon={<DescriptionOutlinedIcon />}
              title={t('source_type')}
              expanded={expandedSections.docTypes}
              onToggle={() => toggleSection('docTypes')}
              activeCount={getActiveCount('doc_types')}
              dk={dk}
            />
            <Collapse in={expandedSections.docTypes}>
              <Box sx={{ pb: 1 }}>
                {stableFacets.doc_types.map(({ value, count }) => (
                  <FilterItem
                    key={value}
                    label={t(value)}
                    count={count}
                    checked={isChecked('doc_types', value)}
                    onChange={() => handleFilterToggle('doc_types', value)}
                    color={getDocTypeColor(value)}
                    dk={dk}
                  />
                ))}
              </Box>
            </Collapse>
            <Divider sx={{ mx: 1, my: 0.5, borderColor: dk.border }} />
          </Box>
        )}

        {/* ── Source Platform (Authority) — regulation mode only ──────── */}
        {hasSourcePlatforms && (
          <>
            <SectionHeader
              icon={<AccountTreeOutlinedIcon />}
              title={t('filter_source_platform')}
              expanded={expandedSections.sourcePlatforms}
              onToggle={() => toggleSection('sourcePlatforms')}
              activeCount={getActiveCount('source_platforms')}
              dk={dk}
            />
            <Collapse in={expandedSections.sourcePlatforms}>
              <Box sx={{ pb: 1 }}>
                {stableFacets.source_platforms.map(({ value, count }) => (
                  <FilterItem
                    key={value}
                    label={value}
                    count={count}
                    checked={isChecked('source_platforms', value)}
                    onChange={() => handleFilterToggle('source_platforms', value)}
                    dk={dk}
                  />
                ))}
              </Box>
            </Collapse>
            <Divider sx={{ mx: 1, my: 0.5, borderColor: dk.border }} />
          </>
        )}

        {/* ── Jurisdiction (3-level tree) ─────────────────────────────── */}
        {hasJurisdiction && (
          <>
            <SectionHeader
              icon={<AccountBalanceOutlinedIcon />}
              title={t('jurisdiction')}
              expanded={expandedSections.jurisdiction}
              onToggle={() => toggleSection('jurisdiction')}
              activeCount={getActiveCount('jurisdiction_names')}
              dk={dk}
            />
            <Collapse in={expandedSections.jurisdiction}>
              <Box sx={{ pb: 1 }}>
                {/* Flat jurisdictions not in UAE tree (e.g. "Kingdom of Morocco") */}
                {flatJurisdictions.map(({ value, count }) => (
                  <FilterItem
                    key={value}
                    label={value}
                    count={count}
                    checked={isChecked('jurisdiction_names', value)}
                    onChange={() => handleFilterToggle('jurisdiction_names', value)}
                    dk={dk}
                  />
                ))}
                {jurisdictionTree.map((group) => {
                  const groupKey = group.level
                  const isGroupExpanded = expandedJurisdictionGroups[groupKey] !== false

                  if (group.level === 'free_zone') {
                    const hasAnyFreeZone = group.children.some((parentGroup) =>
                      parentGroup.zones.some((z) => jurisdictionNameCounts[z.name] > 0)
                    )
                    if (!hasAnyFreeZone) return null

                    return (
                      <React.Fragment key={groupKey}>
                        <Box
                          onClick={() => toggleJurisdictionGroup(groupKey)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 0.5,
                            px: 1,
                            cursor: 'pointer',
                            borderRadius: '6px',
                            '&:hover': {
                              backgroundColor: dk.hover,
                            },
                          }}
                        >
                          {isGroupExpanded ? (
                            <ExpandMoreIcon sx={{ fontSize: '0.9rem', color: dk.disabled }} />
                          ) : (
                            <ChevronRightIcon sx={{ fontSize: '0.9rem', color: dk.disabled }} />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.74rem',
                              fontWeight: 600,
                              color: dk.text2,
                            }}
                          >
                            {t(group.label)}
                          </Typography>
                        </Box>

                        <Collapse in={isGroupExpanded}>
                          {group.children.map((parentGroup) => {
                            const parentKey = `fz-${parentGroup.parent}`
                            const isParentExpanded = expandedJurisdictionGroups[parentKey] !== false
                            const hasZones = parentGroup.zones.some(
                              (z) => jurisdictionNameCounts[z.name] > 0
                            )
                            if (!hasZones) return null

                            return (
                              <React.Fragment key={parentKey}>
                                <Box
                                  onClick={() => toggleJurisdictionGroup(parentKey)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    py: 0.3,
                                    px: 1,
                                    pl: 3,
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    '&:hover': {
                                      backgroundColor: dk.hover,
                                    },
                                  }}
                                >
                                  {isParentExpanded ? (
                                    <ExpandMoreIcon sx={{ fontSize: '0.8rem', color: dk.disabled }} />
                                  ) : (
                                    <ChevronRightIcon sx={{ fontSize: '0.8rem', color: dk.disabled }} />
                                  )}
                                  <Typography
                                    sx={{
                                      fontSize: '0.72rem',
                                      fontWeight: 500,
                                      color: dk.text2,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {parentGroup.parent}
                                  </Typography>
                                </Box>

                                <Collapse in={isParentExpanded}>
                                  {parentGroup.zones.map((zone) => {
                                    const count = jurisdictionNameCounts[zone.name] || 0
                                    if (count === 0) return null
                                    return (
                                      <FilterItem
                                        key={zone.name}
                                        label={zone.name}
                                        count={count}
                                        checked={isChecked('jurisdiction_names', zone.name)}
                                        onChange={() =>
                                          handleFilterToggle('jurisdiction_names', zone.name)
                                        }
                                        dk={dk}
                                        indent={2}
                                      />
                                    )
                                  })}
                                </Collapse>
                              </React.Fragment>
                            )
                          })}
                        </Collapse>
                      </React.Fragment>
                    )
                  }

                  // Federal / Emirates — flat children list
                  const visibleChildren = group.children.filter(
                    (child) => jurisdictionNameCounts[child.name] > 0
                  )
                  if (visibleChildren.length === 0) return null

                  return (
                    <React.Fragment key={groupKey}>
                      <Box
                        onClick={() => toggleJurisdictionGroup(groupKey)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          py: 0.5,
                          px: 1,
                          cursor: 'pointer',
                          borderRadius: '6px',
                          '&:hover': {
                            backgroundColor: dk.hover,
                          },
                        }}
                      >
                        {isGroupExpanded ? (
                          <ExpandMoreIcon sx={{ fontSize: '0.9rem', color: dk.disabled }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: '0.9rem', color: dk.disabled }} />
                        )}
                        <Typography
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: dk.text2,
                          }}
                        >
                          {t(group.label)}
                        </Typography>
                      </Box>

                      <Collapse in={isGroupExpanded}>
                        {visibleChildren.map((child) => (
                          <FilterItem
                            key={child.name}
                            label={child.name}
                            count={jurisdictionNameCounts[child.name] || 0}
                            checked={isChecked('jurisdiction_names', child.name)}
                            onChange={() =>
                              handleFilterToggle('jurisdiction_names', child.name)
                            }
                            dk={dk}
                            indent={1}
                          />
                        ))}
                      </Collapse>
                    </React.Fragment>
                  )
                })}
              </Box>
            </Collapse>
            <Divider sx={{ mx: 1, my: 0.5, borderColor: dk.border }} />
          </>
        )}

        {/* ── Case Type Category ──────────────────────────────────────── */}
        {hasCaseTypeCategories && (
          <>
            <SectionHeader
              icon={<CategoryOutlinedIcon />}
              title={t('case_type')}
              expanded={expandedSections.caseTypeCategories}
              onToggle={() => toggleSection('caseTypeCategories')}
              activeCount={getActiveCount('case_type_categories')}
              dk={dk}
            />
            <Collapse in={expandedSections.caseTypeCategories}>
              <Box sx={{ pb: 1 }}>
                {stableFacets.case_type_categories.map(({ value, count }) => (
                  <FilterItem
                    key={value}
                    label={t(`case_type_${value}`, value)}
                    count={count}
                    checked={isChecked('case_type_categories', value)}
                    onChange={() => handleFilterToggle('case_type_categories', value)}
                    dk={dk}
                  />
                ))}
              </Box>
            </Collapse>
            {hasLegalDomains && <Divider sx={{ mx: 1, my: 0.5, borderColor: dk.border }} />}
          </>
        )}

        {/* ── Field of Law ───────────────────────────────────────────── */}
        {hasLegalDomains && (
          <>
            <SectionHeader
              icon={<GavelOutlinedIcon />}
              title={t('field_of_law')}
              expanded={expandedSections.legalDomains}
              onToggle={() => toggleSection('legalDomains')}
              activeCount={getActiveCount('legal_domains')}
              dk={dk}
            />
            <Collapse in={expandedSections.legalDomains}>
              <Box sx={{ pb: 1 }}>
                {stableFacets.legal_domains.map(({ value, count }) => (
                  <FilterItem
                    key={value}
                    label={t(value)}
                    count={count}
                    checked={isChecked('legal_domains', value)}
                    onChange={() => handleFilterToggle('legal_domains', value)}
                    dk={dk}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  )
}

export default SearchFilterSidebar
